import { DateTime } from "luxon";
import type { BackgroundJob, BackgroundJobField, BackgroundJobKind } from "@/api/background-job/_models/BackgroundJob";
import { formatDateTime } from "@/app/date.utils";
import type { ListGridExpandedField } from "@/components/list-grid/ListGridExpandedFields";

const TRIGGER_LABEL: Record<BackgroundJob["trigger"], string> = {
  MANUAL: "Someone",
  SCHEDULE: "Schedule",
};

const toInputValueText = (field: BackgroundJobField, value: unknown): string => {
  if (value === undefined || value === null || value === "") return "—";
  const text = String(value);
  if (field.type === "select") return field.options.find((option) => option.value === text)?.label ?? text;
  if (field.type === "day" && /^\d{6}$/.test(text)) return DateTime.fromFormat(text, "yyMMdd").toFormat("d LLL yyyy");
  if (field.type === "isoDate") return formatDateTime(text);
  return text;
};

/** A job's inputs as labelled values, by its kind's fields; empty when the kind takes none. */
export const toJobInputFieldList = (job: BackgroundJob, kind: BackgroundJobKind | undefined): ListGridExpandedField[] => {
  const input = (job.input ?? {}) as Record<string, unknown>;
  return (kind?.fields ?? []).map((field) => ({
    label: field.label,
    value: toInputValueText(field, input[field.name]),
  }));
};

/** When and how a job ran: its trigger, when it was queued, started and finished, and how long it took. */
export const toJobRunFieldList = (job: BackgroundJob, duration: string): ListGridExpandedField[] => {
  return [
    { label: "Started by", value: TRIGGER_LABEL[job.trigger] },
    { label: "Queued", value: formatDateTime(job.createdAt) },
    { label: "Started", value: formatDateTime(job.startedAt) },
    { label: "Finished", value: formatDateTime(job.finishedAt) },
    { label: "Took", value: duration || "—" },
  ];
};
